import { MachinePublicHydrated } from "@aura-frontend/omnibus-az-client";
import {
  Button,
  Icons,
  isPopulatedArray,
  Skeleton,
} from "@win-reality/react-ui";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { ViewToggleButton } from "@/components/ui/ViewToggleButton";
import { ROUTES } from "@/lib/routes";

import { useComplexesStore } from "../../Complexes.store";
import { ActiveEvent } from "../ActiveEvent";
import { FieldCalendar } from "../FieldCalendar/FieldCalendar";
import { NoActiveEvent } from "../NoActiveEvent";
import { UpcomingEventItem } from "../UpcomingEventItem";
import {
  getMonthForOffset,
  useScheduledEventsForMonthRange,
} from "./FieldSchedule.queries";

type FieldScheduleProps = {
  complexName: string;
  machineField?: MachinePublicHydrated;
  isLoading?: boolean;
};

function FieldSchedule({
  complexName,
  machineField,
  isLoading,
}: FieldScheduleProps) {
  const {
    upcomingEventsView: upcomingView,
    setUpcomingEventsView: setUpcomingView,
  } = useComplexesStore();

  const [monthCount, setMonthCount] = useState(1);

  useEffect(() => {
    setMonthCount(1);
  }, [machineField?.machineUuid]);

  const {
    events: upcomingEvents,
    isInitialLoading: isLoadingEvents,
    isFetchingMore,
  } = useScheduledEventsForMonthRange({
    fieldIds: machineField ? [machineField.machineUuid] : [],
    monthCount,
    fromNow: true,
    refetchIntervalMs: 30_000,
  });

  const nextMonth = getMonthForOffset(monthCount);
  const nextMonthLabel = new Date(
    nextMonth.year,
    nextMonth.month,
    1,
  ).toLocaleString("default", { month: "long" });

  return (
    <div className="px-5 py-6 mx-auto flex flex-col gap-8">
      <div className="flex flex-col gap-3 w-full max-w-130 mx-auto">
        <div className="uppercase text-xs text-gray-400 mt-2">
          Field Activity
        </div>

        {isLoading ? (
          <Skeleton className="rounded h-40" />
        ) : (
          <>
            {machineField?.currentGame && (
              <ActiveEvent
                machineField={machineField}
                event={machineField.currentGame}
              />
            )}

            {machineField && !machineField?.currentGame && (
              <NoActiveEvent machineField={machineField} />
            )}
          </>
        )}
      </div>

      <div>
        <div className="flex justify-between items-start max-w-130 mx-auto">
          <div className="uppercase text-xs text-gray-400 mb-3 mt-2">
            Upcoming Events
          </div>

          <div className="flex gap-2 justify-end items-center">
            <ViewToggleButton
              isActive={upcomingView === "calendar"}
              icon={Icons.CalendarOutline}
              onClick={() => setUpcomingView("calendar")}
              size="small"
            />

            <ViewToggleButton
              isActive={upcomingView === "list"}
              icon={Icons.Bars3BottomLeftOutline}
              onClick={() => setUpcomingView("list")}
              size="small"
            />

            <Link
              to={
                complexName && machineField
                  ? ROUTES.NEW_EVENT.replace(
                      ":fieldId",
                      machineField.machineUuid,
                    ).replace(":complexName", complexName)
                  : "#"
              }
            >
              <Button size="sm" variant="secondary-flat-light" className="ml-4">
                <Icons.Plus className="size-4" />
                Schedule Event
              </Button>
            </Link>
          </div>
        </div>

        <>
          {upcomingView === "calendar" && (
            <div className="my-5">
              <FieldCalendar field={machineField} />
            </div>
          )}

          {upcomingView === "list" && (
            <div className="max-w-130 mx-auto">
              {!isPopulatedArray(upcomingEvents) &&
                (isLoadingEvents || isLoading) && (
                  <Skeleton className="rounded h-40 max-w-130 mx-auto mt-5" />
                )}

              {!isPopulatedArray(upcomingEvents) &&
                !(isLoadingEvents || isLoading) && (
                  <div className="text-sm flex items-center justify-center h-32 ">
                    No upcoming events scheduled for this field.
                  </div>
                )}

              {isPopulatedArray(upcomingEvents) && (
                <div className="mt-5 flex flex-col gap-3">
                  {upcomingEvents.map((event) => (
                    <UpcomingEventItem
                      key={event.id}
                      event={event}
                      currentGameUuid={machineField?.currentGame?.gameUuid}
                    />
                  ))}

                  <div className="flex items-center justify-center mt-4 mb-8">
                    <Button
                      isLoading={isFetchingMore}
                      variant="secondary-flat-dark-outline"
                      size="sm-square"
                      isDisabled={isFetchingMore}
                      onClick={() => setMonthCount((c) => c + 1)}
                    >
                      {`Load More (${nextMonthLabel})`}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      </div>
    </div>
  );
}

export { FieldSchedule };
